import Loader from '@/components/Loader';
import MaterialCard from '@/components/MaterialCard';
import { VideoModal } from '@/components/VideoModal';
import BaseLayout from '@/layout/BaseLayout';
import { trpc, trpcClient } from '@/trpcClient';
import { cn } from '@/utils/utils';
import type { ListMaterialFilterTypeWithValues } from '@shelflix/schemas/material/list';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MaterialListFilter } from './MaterialListFilter';
import { SummaryDashboard, SummaryProps } from '@/components/SummaryDash';
import cloudOffSvg from '@/assets/icons-normalized/cloud-off.svg?raw';
import onlineSvg from '@/assets/icons-normalized/online.svg?raw';
import clockZzSvg from '@/assets/icons-normalized/clock-zz.svg?raw';
import { MaterialTable } from '@/components/MaterialTable';
import { MaterialDto } from '@shelflix/trpc/routes/material/list/useCase';

export function MaterialList() {
  const location = useLocation();
  const hasShownToast = useRef(false);
  const [filters, setFilters] = useState<ListMaterialFilterTypeWithValues>({});

  const [videoUrls, setVideoUrls] = useState<string[]>([]);
  const videoTriggerRef = useRef<HTMLButtonElement | null>(null);

  const [groupedMaterials, setGroupedMaterials] = useState<Record<string, MaterialDto[]>>({});

  const navigate = useNavigate();

  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    ...trpc.material.list.queryOptions({
      page,
      ...(filters.search?.value && { search: { ...filters.search } }),
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
      ...(filters.sort && { sort: filters.sort }),
      recordsPerPage: 9999,
    }),
    refetchInterval: 180_000, // 3 minutes — keep status in sync with device heartbeats
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    const materials = data?.materials ?? [];
    setGroupedMaterials(
      materials.reduce(
        (acc, material) => {
          if (!acc[material.facility.uuid]) {
            acc[material.facility.uuid] = [];
          }
          acc[material.facility.uuid].push(material);
          return acc;
        },
        {} as Record<string, MaterialDto[]>
      )
    );
  }, [data]);

  useEffect(() => {
    if (location.state?.toastSuccess && !hasShownToast.current) {
      toast.success(`Alteração salva com sucesso${location.state.code ? ' (' + location.state.code + ')' : ''}`, {
        duration: 10000,
      });
      hasShownToast.current = true;

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Aplica filtro inicial se vier do dashboard
  useEffect(() => {
    if (location.state?.searchFilter && !filters.search?.value) {
      setFilters((prev) => ({
        ...prev,
        search: location.state.searchFilter,
      }));
    }
  }, [location.state, navigate, filters.search?.value]);

  // Build initial filters from location state
  const initialStatusFilter = location.state?.statusFilter
    ? [
        {
          field: {
            name: 'status' as const,
            condition: 'equals' as const,
            value: location.state.statusFilter,
          },
        },
      ]
    : undefined;

  const handleOpenVideoModal = async (materialId: string) => {
    try {
      const response = await trpcClient.material.get.query({
        uuid: materialId,
      });
      if (!response.currentCampaign) {
        toast.error('Material não está associado a nenhuma campanha ativa no momento.');
        return;
      }
      setVideoUrls(response.currentCampaign.medias.map((item) => item.mediaUrl));
      setTimeout(() => {
        videoTriggerRef.current?.click();
      }, 0);
    } catch (err) {
      toast.error(`Erro ao buscar vídeos: ${err}`);
    }
  };

  //Data for Summary Dashboard
  const {
    data: countByStatus = [],
    isLoading: _isLoadingCountByStatus,
    isError: _isErrorCountByStatus,
  } = useQuery({
    ...trpc.material.count.queryOptions({
      grouping: 'status',
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
    }),
    placeholderData: keepPreviousData,
  });

  const statusArray = Array.isArray(countByStatus) ? countByStatus : [];
  const materialSumarryData: SummaryProps = {
    overview: {
      title: 'Total',
      amount: statusArray
        .filter(
          (item) =>
            item.group?.toLowerCase() === 'active' ||
            item.group?.toLowerCase() === 'operationalonline' ||
            item.group?.toLowerCase() === 'offline'
        )
        .reduce((sum, item) => sum + (item.count || 0), 0),
    },
    summary: [
      {
        title: 'Online',
        count: statusArray
          .filter((item) => item.group?.toLowerCase() === 'active' || item.group?.toLowerCase() === 'operationalonline')
          .reduce((sum, item) => sum + (item.count || 0), 0),
        statusFilter: 'ACTIVE',
        icon: onlineSvg,
      },
      {
        title: 'Offline',
        count: statusArray.find((item) => item.group?.toLowerCase() === 'offline')?.count || 0,
        statusFilter: 'OFFLINE',
        icon: cloudOffSvg,
      },
      {
        title: 'Indisponível',
        count: statusArray
          .filter((item) => item.group?.toLowerCase() === 'maintenance' || item.group?.toLowerCase() === 'standby')
          .reduce((sum, item) => sum + (item.count || 0), 0),
        statusFilter: 'MAINTENANCE',
        icon: clockZzSvg,
      },
    ],
  };

  //Filters
  const {
    data: filtersData = { state: [], brand: [], material: [] },
    isLoading: _isLoadingFilters,
    isFetching: _isFetchingFilters,
    isError: _isErrorFilters,
  } = useQuery({
    ...trpc.dashboard.filters.queryOptions({
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    placeholderData: keepPreviousData,
  });

  const [displayListAsGrid, setDisplayListAsGrid] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const savedView = window.localStorage.getItem('material-list-view-mode');
    if (savedView === 'table') return false;
    return true; // default: grid
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('material-list-view-mode', displayListAsGrid ? 'grid' : 'table');
  }, [displayListAsGrid]);

  const [currentPage, setCurrentPage] = useState(1);

  return (
    <BaseLayout>
      <section className="flex flex-1 flex-col gap-6">
        <SummaryDashboard summaryData={materialSumarryData} enableHorizontal={true} redirect={false} />
        <MaterialListFilter
          onChange={(filters) => {
            setFilters(filters);
            setPage(1);
            setCurrentPage(1);
          }}
          initialValue={location.state?.searchFilter}
          initialFilters={initialStatusFilter}
          states={filtersData.state}
          brands={filtersData.brand}
          materials={filtersData.material}
          displayListAsGrid={displayListAsGrid}
          setDisplayListAsGrid={(value) => {
            setDisplayListAsGrid(value);
            setCurrentPage(1);
          }}
        />
        <section className="h-full w-full">
          {isLoading && <Loader message="Carregando materiais de PDV..." />}
          {isError && <p className="p-4 text-red-500">Erro ao carregar os materiais de PDV.</p>}
          {!isLoading &&
            !isError &&
            (displayListAsGrid ? (
              <div className={cn('grid justify-start gap-3 px-2 min-[1040px]:grid-cols-2')}>
                {Object.entries(groupedMaterials)
                  .sort((a, b) => a[1][0].facility.name.localeCompare(b[1][0].facility.name))
                  .map(([facilityUid, material]) => (
                    <MaterialCard
                      key={facilityUid}
                      materials={material}
                      establishment={material[0].facility.name}
                      handleOpenVideoModal={handleOpenVideoModal}
                    />
                  ))}
              </div>
            ) : (
              <MaterialTable currentPage={currentPage} setCurrentPage={setCurrentPage} data={data?.materials || []} />
            ))}
        </section>

        <VideoModal videos={videoUrls} trigger={<button ref={videoTriggerRef} className="hidden" />} />
      </section>
    </BaseLayout>
  );
}
